import { useMemo, useRef, useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { Command } from 'cmdk'
import { ArrowRight, Check, ChevronDown, History, Loader2, Search } from 'lucide-react'
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover'
import { getScreenerDiscoverySnapshot } from '@/lib/api'
import { createTickerSearchMatcher } from '@/lib/tickerSearch'
import { cn } from '@/lib/utils'
import type { ScreenerTicker } from '@/types'

interface ScreenerTickerPickerProps {
  activeTicker: string | null
  tickers: ScreenerTicker[]
  exchangeOptions: string[]
  disabled: boolean
  onSelectTicker: (symbol: string) => void
  onAddTicker: (symbol: string) => Promise<boolean>
  onOpenHistory: () => void
}

export function ScreenerTickerPicker({
  activeTicker, tickers, exchangeOptions, disabled, onSelectTicker, onAddTicker, onOpenHistory,
}: ScreenerTickerPickerProps) {
  const [open, setOpen] = useState(false)
  const [search, setSearch] = useState('')
  const [adding, setAdding] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const addPending = useRef(false)
  const exchanges = useMemo(() => Array.from(new Set(
    exchangeOptions.map((exchange) => exchange.trim().toLowerCase()).filter(Boolean),
  )).sort(), [exchangeOptions])
  // Use every OI exchange, independently of Discovery's saved filters. The
  // shared snapshot cache avoids fetching again for each search keystroke.
  const snapshot = useQuery({
    queryKey: ['screener-discovery-snapshot', exchanges, '24h'],
    queryFn: ({ signal }) => getScreenerDiscoverySnapshot(exchanges, signal),
    enabled: open && exchanges.length > 0,
    staleTime: 10_000,
  })
  const historySymbols = useMemo(() => new Set(tickers.map((ticker) => ticker.symbol)), [tickers])
  const choices = useMemo(() => {
    const symbols = new Set(tickers.map((ticker) => ticker.symbol))
    for (const item of snapshot.data?.items ?? []) symbols.add(item.symbol)
    const all = Array.from(symbols)
    const matches = createTickerSearchMatcher(search, all)
    const query = search.trim().toUpperCase()
    return all.filter(matches).sort((left, right) => {
      if (query) {
        const exact = Number(right === query) - Number(left === query)
        if (exact) return exact
        // Base symbols precede longer substring matches for pair searches.
        return left.length - right.length || left.localeCompare(right)
      }
      return Number(historySymbols.has(right)) - Number(historySymbols.has(left)) || left.localeCompare(right)
    })
  }, [tickers, snapshot.data?.items, search, historySymbols])
  const visibleChoices = choices.slice(0, 100)
  const query = search.trim().toUpperCase()
  const busy = disabled || adding

  const changeOpen = (next: boolean) => {
    setOpen(next)
    if (next) {
      setSearch('')
      setError(null)
    }
  }

  const selectTicker = async (symbol: string) => {
    if (!symbol || disabled || addPending.current) return
    setError(null)
    if (historySymbols.has(symbol)) {
      onSelectTicker(symbol)
      setOpen(false)
      return
    }
    addPending.current = true
    setAdding(true)
    try {
      // Catalog entries are already base symbols. Add an explicit quote so
      // the backend removes it once and preserves names such as TUSD.
      const input = choices.includes(symbol) ? `${symbol}USDT` : symbol
      if (await onAddTicker(input)) setOpen(false)
      else setError(`Could not open ${symbol}. Please try again.`)
    } catch {
      setError(`Could not open ${symbol}. Please try again.`)
    } finally {
      addPending.current = false
      setAdding(false)
    }
  }

  return (
    <Popover open={open} onOpenChange={changeOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          title="Search and select ticker"
          aria-label={activeTicker ? `Select ticker, current ${activeTicker}` : 'Select ticker'}
          className={cn(
            'flex items-center gap-1.5 rounded-l-md border border-r-0 border-border/50 px-3 py-1.5 text-lg font-bold tracking-tight transition-colors hover:bg-muted/30',
            activeTicker ? 'text-foreground' : 'text-muted-foreground',
          )}
        >
          {activeTicker || 'Select Ticker'}
          <ChevronDown className="h-3.5 w-3.5 text-muted-foreground" aria-hidden="true" />
        </button>
      </PopoverTrigger>
      <PopoverContent align="start" className="w-80 max-w-[calc(100vw-2rem)] overflow-hidden border-slate-800 bg-[#080d14] p-0 text-slate-200">
        <Command label="Select ticker" shouldFilter={false} loop>
          <div className="flex items-center gap-2 border-b border-slate-800 px-3">
            <Search className="h-4 w-4 shrink-0 text-teal-400" aria-hidden="true" />
            <Command.Input
              autoFocus
              aria-label="Search all tickers"
              placeholder="Ticker or pair, e.g. FFUSDT"
              autoComplete="off"
              spellCheck={false}
              value={search}
              onValueChange={(value) => { setSearch(value); setError(null) }}
              readOnly={adding}
              aria-disabled={adding}
              className="h-11 w-full bg-transparent text-sm outline-none placeholder:text-slate-500 read-only:opacity-50"
            />
            {adding && <Loader2 className="h-4 w-4 shrink-0 animate-spin text-teal-400" aria-label="Opening ticker" />}
          </div>
          <div className="px-3 py-2 text-[10px] text-slate-500">Search all tickers · Discovery filters do not apply</div>
          {error && <div role="alert" className="px-3 pb-2 text-xs text-rose-400">{error}</div>}
          {(snapshot.isError || snapshot.data?.ok === false) && <div role="status" className="px-3 pb-2 text-xs text-amber-400">Ticker suggestions unavailable. You can still enter a ticker to open it.</div>}
          {snapshot.isLoading && <div role="status" className="px-3 pb-2 text-xs text-slate-500">Loading tickers…</div>}
          <Command.List className="max-h-72 overflow-y-auto p-1" aria-label="Ticker search results" aria-busy={adding}>
            {visibleChoices.map((symbol) => (
              <Command.Item
                key={symbol}
                value={symbol}
                disabled={busy}
                onSelect={() => void selectTicker(symbol)}
                className="flex cursor-pointer items-center gap-2 rounded px-2 py-2 text-xs data-[selected=true]:bg-teal-400/10 data-[selected=true]:text-teal-300 data-[disabled=true]:opacity-50"
              >
                <span className="min-w-0 flex-1 truncate font-semibold">{symbol}</span>
                {historySymbols.has(symbol) && <History className="h-3 w-3 text-slate-500" aria-label="In history" />}
                {symbol === activeTicker && <Check className="h-3.5 w-3.5 text-teal-400" aria-label="Selected" />}
              </Command.Item>
            ))}
            {query && !choices.includes(query) && (
              <Command.Item
                value={`open:${query}`}
                disabled={busy}
                onSelect={() => void selectTicker(query)}
                className="flex cursor-pointer items-center gap-2 rounded px-2 py-2 text-xs text-teal-300 data-[selected=true]:bg-teal-400/10 data-[disabled=true]:opacity-50"
              >
                <ArrowRight className="h-3.5 w-3.5 shrink-0" aria-hidden="true" />
                <span className="truncate">Open {query}</span>
              </Command.Item>
            )}
            {!query && choices.length === 0 && !snapshot.isLoading && (
              <div className="px-2 py-5 text-center text-xs text-slate-500">Enter a ticker to open its charts.</div>
            )}
          </Command.List>
          {choices.length > visibleChoices.length && <div className="px-3 pb-2 text-[10px] text-slate-500">{choices.length} matches · Keep typing to narrow the list</div>}
        </Command>
        <button
          type="button"
          onClick={() => { setOpen(false); onOpenHistory() }}
          className="flex w-full items-center gap-2 border-t border-slate-800 px-3 py-2.5 text-xs text-slate-400 hover:bg-slate-900 hover:text-slate-200"
        >
          <History className="h-3.5 w-3.5" aria-hidden="true" />
          Open ticker history
          <span className="ml-auto text-[10px] text-slate-500">{tickers.length}</span>
        </button>
      </PopoverContent>
    </Popover>
  )
}
